/**
 * overviewStats — PURE aggregation behind the Overview tab's build-spend
 * summary and version timeline. Kept free of React so the token/cost math is
 * unit-testable.
 *
 * SCOPE (Phase 1): this covers only the tokens each BUILD stamps on its version
 * doc (`tokenUsage`) — the dominant LLM cost of a game. Asset-generation and
 * concept-analysis spend live in the superadmin-only `llmRequests` log and are
 * a Phase-2 fast-follow (a creator-scoped callable), so the panel labels this
 * "build & iteration" spend rather than an all-in total.
 *
 * Cost is READ from the version's stamped `costUsd`, never recomputed here.
 * This used to price `tokenUsage` against the live @djgg/llm rate table, which
 * made it the one surface that restated itself whenever a rate changed while
 * Stats, /admin/stats and the build trace kept showing the value stored at
 * write time. Static rates hid the split until the opus-4-8 correction
 * (2026-07-29) would have made the same build show two different numbers on
 * two tabs. The builder stamps cost per call at the serving model's rate; this
 * sums what it stamped.
 */
import type { CreationVersion } from '@djgg/creations';

export interface BuildSpend {
  /** Versions that carried token usage (what the totals sum over). */
  builds: number;
  inputTokens: number;
  outputTokens: number;
  /** Sum of the versions' stamped `costUsd`. */
  costUsd: number;
  /** False when a counted build spent tokens but stamped no cost — an unpriced
   *  model, or a version predating the stamp that the backfill has not reached.
   *  Cost is then a FLOOR, not exact, and the UI says so. */
  allPriced: boolean;
}

/** Sum the token/cost figures across a creation's versions. */
export function summarizeBuildSpend(versions: CreationVersion[]): BuildSpend {
  let builds = 0;
  let inputTokens = 0;
  let outputTokens = 0;
  let costUsd = 0;
  let allPriced = true;
  for (const v of versions) {
    const usage = v.tokenUsage;
    if (!usage || (usage.input === 0 && usage.output === 0)) continue;
    builds += 1;
    inputTokens += usage.input;
    outputTokens += usage.output;
    costUsd += v.costUsd ?? 0;
    // Tokens burned but nothing stamped ⇒ this build's cost is not represented.
    if (!v.costUsd) allPriced = false;
  }
  return { builds, inputTokens, outputTokens, costUsd, allPriced };
}

/** One bar in the version timeline chart (oldest→newest). */
export interface TimelinePoint {
  n: number;
  createdAt: number;
  status: CreationVersion['buildStatus'];
  tokens: number;
  active: boolean;
}

/**
 * Project versions into ascending-by-version timeline points. `activeVersion`
 * marks the currently-served build. Input may be in any order (the versions
 * subscription is newest-first); output is always oldest→newest.
 */
export function buildTimeline(
  versions: CreationVersion[],
  activeVersion: number,
): TimelinePoint[] {
  return [...versions]
    .sort((a, b) => a.n - b.n)
    .map((v) => ({
      n: v.n,
      createdAt: v.createdAt,
      status: v.buildStatus,
      tokens: (v.tokenUsage?.input ?? 0) + (v.tokenUsage?.output ?? 0),
      active: v.n === activeVersion,
    }));
}
